import React from 'react';
import { Loader2 } from 'lucide-react';
import clsx from 'clsx';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  href?: string;
  children: React.ReactNode;
}

const variantClasses = {
  primary:
    'bg-primary text-white hover:bg-primary-dark shadow hover:shadow-md',
  secondary:
    'border-2 border-primary text-primary hover:bg-primary/5 shadow-sm hover:shadow',
  danger:
    'bg-danger text-white hover:bg-red-600 shadow hover:shadow-md',
  ghost:
    'text-text-secondary hover:bg-surface-secondary hover:text-text-primary',
};

const sizeClasses = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2',
  lg: 'px-6 py-3 text-lg',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  href,
  className,
  children,
  type = 'button',
  onClick,
  ...props
}) => {
  const baseClasses = clsx(
    'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-150',
    'focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2',
    'active:scale-[0.98]',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
    variantClasses[variant],
    sizeClasses[size],
    className
  );

  const content = (
    <>
      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {children}
    </>
  );

  if (href) {
    return (
      <a href={href} className={baseClasses}>
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={baseClasses}
      {...props}
    >
      {content}
    </button>
  );
};

export default Button;